import React, { useEffect, useMemo, useState, useCallback, memo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import HomeIcon from "@mui/icons-material/Home"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import InventoryIcon from "@mui/icons-material/Inventory"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import RefreshIcon from "@mui/icons-material/Refresh"
import AssessmentIcon from "@mui/icons-material/Assessment"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import PrintIcon from "@mui/icons-material/Print"
import CategoryIcon from "@mui/icons-material/Category"
import {
  fetchProductionOrders,
  clearProductionCache,
} from "store/production/actions"
import {
  getOrders,
  patchOrder,
  getProducts,
  patchProduct,
  postLoan,
} from "helpers/api_helper"
import { useAppConfig } from "../../context/AppConfigContext"

const FILTER_TABS = [
  { value: "TO-PACKAGE", label: "To Package", icon: <InventoryIcon /> },
  { value: "PACKAGED", label: "Packaged", icon: <CheckCircleIcon /> },
]

const getStatusColor = (status) => {
  switch (status) {
    case "TO-PACKAGE":
      return { bg: "#fef3c7", color: "#d97706", label: "To Package" }
    case "PACKAGED":
      return { bg: "#dcfce7", color: "#16a34a", label: "Packaged" }
    default:
      return { bg: "#f3f4f6", color: "#6b7280", label: status }
  }
}

// Memoized Status Chip
const StatusChip = memo(({ status, t }) => {
  const statusInfo = getStatusColor(status)
  return (
    <Chip
      label={t(statusInfo.label)}
      size="small"
      sx={{
        bgcolor: statusInfo.bg,
        color: statusInfo.color,
        fontWeight: 500,
        fontSize: "0.75rem",
      }}
    />
  )
})

// Memoized Analytics Panel
const AnalyticsPanel = memo(({ analytics, expandedPanels, setExpandedPanels, openOrder, t }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
    <Box
      sx={{
        px: 3, py: 2,
        borderBottom: expandedPanels.analytics ? "1px solid #f3f4f6" : "none",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        cursor: "pointer", bgcolor: "#f8fafc",
      }}
      onClick={() => setExpandedPanels((prev) => ({ ...prev, analytics: !prev.analytics }))}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: "#8b5cf6", color: "#fff", width: 40, height: 40 }}>
          <AssessmentIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#111827">
            {t("Packaging Analytics")}
          </Typography>
          <Typography variant="caption" color="#6b7280">
            {t("Packaging progress and workload overview")}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {analytics.overdueOrders > 0 && (
          <Chip
            icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
            label={`${analytics.overdueOrders} ${t("Overdue")}`}
            size="small"
            sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 500 }}
          />
        )}
        <IconButton size="small">
          {expandedPanels.analytics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
    </Box>
    <Collapse in={expandedPanels.analytics}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Completion Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: analytics.completionRate >= 50 ? "#dcfce7" : "#fef3c7",
                    color: analytics.completionRate >= 50 ? "#16a34a" : "#d97706",
                    width: 48, height: 48,
                  }}
                >
                  <TrendingUpIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.completionRate}%
                  </Typography>
                  <Typography variant="caption" color="#6b7280">{t("Packaged")}</Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={analytics.completionRate}
                sx={{
                  mt: 2, height: 6, borderRadius: 3, bgcolor: "#f3f4f6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: analytics.completionRate >= 50 ? "#16a34a" : "#d97706",
                    borderRadius: 3,
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography variant="caption" color="#16a34a" fontWeight={500}>
                  {analytics.packagedCount} {t("Done")}
                </Typography>
                <Typography variant="caption" color="#d97706" fontWeight={500}>
                  {analytics.toPackageCount} {t("Pending")}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 48, height: 48 }}>
                  <AccessTimeIcon />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  {t("Delivery Timeline")}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Due Today")}</Typography>
                  <Chip
                    label={analytics.ordersDueToday}
                    size="small"
                    sx={{
                      bgcolor: analytics.ordersDueToday > 0 ? "#fef3c7" : "#f3f4f6",
                      color: analytics.ordersDueToday > 0 ? "#d97706" : "#6b7280",
                      fontWeight: 600, minWidth: 32,
                    }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("This Week")}</Typography>
                  <Chip label={analytics.ordersDueThisWeek} size="small" sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 600, minWidth: 32 }} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Overdue")}</Typography>
                  <Chip
                    label={analytics.overdueOrders}
                    size="small"
                    sx={{
                      bgcolor: analytics.overdueOrders > 0 ? "#fef2f2" : "#f3f4f6",
                      color: analytics.overdueOrders > 0 ? "#dc2626" : "#6b7280",
                      fontWeight: 600, minWidth: 32,
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Workload */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", width: 48, height: 48 }}>
                  <CategoryIcon />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  {t("Workload")}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Orders")}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827">{analytics.toPackageCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Products")}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827">{analytics.totalProducts}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Total Items")}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827">{analytics.totalItems}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 48, height: 48 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#111827">{analytics.packagedCount}</Typography>
                  <Typography variant="caption" color="#6b7280">{t("Packaged")}</Typography>
                </Box>
              </Stack>
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f3f4f6" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("To Package")}</Typography>
                  <Chip label={analytics.toPackageCount} size="small" sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 600 }} />
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Urgent Orders */}
        {analytics.urgentOrders.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="#dc2626"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorOutlineIcon fontSize="small" />
              {t("Overdue Orders - Package First!")}
            </Typography>
            <Grid container spacing={1}>
              {analytics.urgentOrders.map((order) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={order.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5, borderRadius: 2, border: "1px solid #fecaca", bgcolor: "#fef2f2",
                      cursor: "pointer", "&:hover": { bgcolor: "#fee2e2" },
                    }}
                    onClick={() => openOrder(order)}
                  >
                    <Typography variant="body2" fontWeight={600} color="#111827">#{order.id}</Typography>
                    <Typography variant="caption" color="#6b7280" noWrap>{order.name}</Typography>
                    <Typography variant="caption" color="#dc2626" display="block">
                      {moment(order.expected_delivery_date).format("MMM DD")}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Collapse>
  </Card>
))

const MuiPackaging = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getCompanyName } = useAppConfig()

  document.title = t("Packaging") + " | FlexFlow ERP"

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState("TO-PACKAGE")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [expandedPanels, setExpandedPanels] = useState({ analytics: true })

  // Modal state
  const [packagerName, setPackagerName] = useState("")
  const [nameError, setNameError] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [infoMsg, setInfoMsg] = useState({ text: "", severity: "info" })

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    try {
      dispatch(clearProductionCache())
      const res = await getOrders(true)
      const allData = Array.isArray(res) ? res : []
      setOrders(allData.filter((o) => ["TO-PACKAGE", "PACKAGED"].includes(o.status)))
    } catch (err) {
      console.error(t("Failed to fetch orders"), err)
    } finally {
      setLoading(false)
    }
  }, [dispatch, t])

  useEffect(() => {
    fetchOrders()
  }, [])

  // Filter and search orders - memoized with debounce
  const filteredOrders = useMemo(() => {
    const subset = orders.filter((o) => o.status === filter)
    if (!debouncedSearch) return subset
    const searchLower = debouncedSearch.toLowerCase()
    return subset.filter((o) =>
      Object.values({
        id: o.id,
        name: o.name,
        status: o.status,
        expected_delivery_date: o.expected_delivery_date,
        delivery_address: o.delivery_address,
      })
        .join(" ")
        .toLowerCase()
        .includes(searchLower)
    )
  }, [orders, filter, debouncedSearch])

  // Analytics - memoized
  const analytics = useMemo(() => {
    const today = moment().startOf("day")
    const endOfWeek = moment().endOf("week")

    const toPackageOrders = orders.filter((o) => o.status === "TO-PACKAGE")
    const packagedOrders = orders.filter((o) => o.status === "PACKAGED")

    const ordersDueToday = toPackageOrders.filter((o) => moment(o.expected_delivery_date).isSame(today, "day"))
    const ordersDueThisWeek = toPackageOrders.filter((o) => moment(o.expected_delivery_date).isBetween(today, endOfWeek, "day", "[]"))
    const overdueOrders = toPackageOrders.filter((o) => moment(o.expected_delivery_date).isBefore(today, "day"))

    let totalProducts = 0, totalItems = 0
    toPackageOrders.forEach((order) => {
      try {
        const products = JSON.parse(order.products || "[]")
        totalProducts += products.length
        totalItems += products.reduce((sum, p) => sum + Number(p.quantity || 0), 0)
      } catch {}
    })

    const totalInPipeline = toPackageOrders.length + packagedOrders.length
    const completionRate = totalInPipeline > 0 ? Math.round((packagedOrders.length / totalInPipeline) * 100) : 0

    return {
      toPackageCount: toPackageOrders.length,
      packagedCount: packagedOrders.length,
      ordersDueToday: ordersDueToday.length,
      ordersDueThisWeek: ordersDueThisWeek.length,
      overdueOrders: overdueOrders.length,
      totalProducts,
      totalItems,
      completionRate,
      urgentOrders: overdueOrders.slice(0, 5),
    }
  }, [orders])

  // Parse products from order
  const parseProducts = useCallback((order) => {
    if (!order) return []
    try {
      return JSON.parse(order.products || "[]")
    } catch {
      return "PARSE_ERROR"
    }
  }, [])

  // Parse loans from order
  const parseLoans = useCallback((order) => {
    if (!order) return []
    try {
      return JSON.parse(order.loans || "[]")
    } catch {
      return "PARSE_ERROR"
    }
  }, [])

  // Open order modal
  const openOrder = useCallback((order) => {
    setSelectedOrder(order)
    setModalOpen(true)
    setPackagerName("")
    setNameError("")
    setInfoMsg({ text: "", severity: "info" })
    setIsProcessing(false)
  }, [])

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false)
    setSelectedOrder(null)
  }, [])

  // Mark order as packaged
  const markPackaged = useCallback(async () => {
    if (!selectedOrder) return

    const name = (packagerName || "").trim()
    if (!name) {
      setNameError(t("Please enter your name to confirm."))
      return
    }
    setNameError("")

    const products = parseProducts(selectedOrder)
    const loans = parseLoans(selectedOrder)

    if (products === "PARSE_ERROR") {
      setInfoMsg({ text: t("Invalid products data. Cannot package this order."), severity: "error" })
      return
    }

    try {
      setIsProcessing(true)
      setInfoMsg({ text: t("Updating product stocks..."), severity: "info" })

      // 1) Decrement stock
      for (const prd of products) {
        const prdInfo = (await getProducts(prd.id))?.[0] || {}
        const currentStock = Number(prdInfo.stock ?? 0)
        const qty = Number(prd.quantity ?? 0)

        if (Number.isNaN(currentStock) || Number.isNaN(qty)) {
          throw new Error(`Invalid stock/quantity for product ${prd.id}`)
        }

        const payload = { resource: [{ id: prd.id, stock: currentStock - qty }] }
        const res = await patchProduct(payload)
        if (res?.error) throw new Error(`Stock update failed for product ${prd.id}: ${res.error}`)
      }

      // 2) Create loan rows
      if (Array.isArray(loans) && loans.length > 0) {
        setInfoMsg({ text: t("Creating loan records..."), severity: "info" })
        for (const prd of loans) {
          const loanPayload = {
            resource: [{
              bound: "OUTBOUND",
              custname: selectedOrder?.name,
              quantity: Number(prd.quantity ?? 0),
              customer_id: selectedOrder?.customer_id ?? selectedOrder?.customer_id_ref ?? null,
              order_id: selectedOrder?.id,
              prd_id: prd.id,
              prdname: prd.name,
            }],
          }
          const loanRes = await postLoan(loanPayload)
          if (loanRes?.error) throw new Error(`Loan post failed for product ${prd.id}: ${loanRes.error}`)
        }
      }

      // 3) Mark order as PACKAGED
      setInfoMsg({ text: t("Finalizing order..."), severity: "info" })
      await patchOrder({ resource: [{ id: selectedOrder.id, status: "PACKAGED", packagedBy: name }] })

      setInfoMsg({ text: t("Order marked as PACKAGED"), severity: "success" })
      await fetchOrders()
      setTimeout(() => closeModal(), 500)
    } catch (err) {
      console.error("Error marking order as PACKAGED:", err)
      setInfoMsg({ text: t("Failed to package the order. Product stocks and loans may be incomplete."), severity: "error" })
    } finally {
      setIsProcessing(false)
    }
  }, [selectedOrder, packagerName, parseProducts, parseLoans, fetchOrders, closeModal, t])

  // Print label
  const printLabel = useCallback(() => {
    if (!selectedOrder) return

    const products = parseProducts(selectedOrder)
    let productsHtml = ""
    if (products === "PARSE_ERROR") {
      productsHtml = `<tr><td colspan="3" style="color:#b00;padding:6px 8px;">${t("Invalid products data")}</td></tr>`
    } else {
      productsHtml = products.map((p) => `
        <tr>
          <td style="padding:4px 8px;border-bottom:1px solid #ddd;">${p.id}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #ddd;">${p.name}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #ddd;text-align:right;">${p.quantity}</td>
        </tr>`).join("")
    }

    const now = moment().format("YYYY-MM-DD HH:mm")
    const packagedBy = (packagerName || "").trim()

    const companyName = getCompanyName()
    const html = `<!doctype html><html><head><meta charset="utf-8" /><title>Order #${selectedOrder.id} - Label</title>
<style>@page { size: A5; margin: 8mm; } body { font-family: Arial, sans-serif; font-size: 11pt; } .label { width: 100%; } .header { border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 8px; } .h-flex { display:flex; justify-content:space-between; align-items:flex-end; } .title { font-size: 16pt; font-weight: bold; } .meta { font-size: 10pt; } .section-title { font-weight:bold; margin:10px 0 6px; } table { border-collapse: collapse; width: 100%; }</style>
</head><body onload="window.print(); setTimeout(() => window.close(), 300);">
  <div class="label"><div class="header"><div class="h-flex"><div class="title">${companyName}</div><div class="meta">${now}</div></div><div class="meta">Order: <strong>#${selectedOrder.id}</strong></div><div class="meta">Customer: <strong>${selectedOrder.name || ""}</strong></div><div class="meta">Packaged by: <strong>${packagedBy || "-"}</strong></div></div>
  <div class="section-title">${t("Products")}</div><table><thead><tr><th style="text-align:left;padding:4px 8px;border-bottom:2px solid #000;">${t("Product ID")}</th><th style="text-align:left;padding:4px 8px;border-bottom:2px solid #000;">${t("Name")}</th><th style="text-align:right;padding:4px 8px;border-bottom:2px solid #000;">${t("Qty")}</th></tr></thead><tbody>${productsHtml}</tbody></table></div>
</body></html>`

    const w = window.open("", "_blank", "width=900,height=650")
    if (!w) return
    w.document.open()
    w.document.write(html)
    w.document.close()
  }, [selectedOrder, packagerName, parseProducts, t, getCompanyName])

  const handleTabChange = useCallback((event, newValue) => {
    setFilter(newValue)
  }, [])

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value)
  }, [])

  const handlePackagerNameChange = useCallback((e) => {
    setPackagerName(e.target.value)
    if (nameError) setNameError("")
  }, [nameError])

  // Memoized columns
  const columns = useMemo(() => [
    {
      field: "id",
      headerName: t("Order ID"),
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="#3b82f6">#{params.value}</Typography>
      ),
    },
    { field: "name", headerName: t("Customer"), flex: 1.5, minWidth: 180 },
    {
      field: "expected_delivery_date",
      headerName: t("Delivery Date"),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => params.value ? moment(params.value).format("YYYY-MM-DD") : "-",
    },
    { field: "delivery_address", headerName: t("Address"), flex: 2, minWidth: 200 },
    {
      field: "status",
      headerName: t("Status"),
      width: 130,
      renderCell: (params) => <StatusChip status={params.value} t={t} />,
    },
  ], [t])

  // Memoized DataGrid styles
  const dataGridSx = useMemo(() => ({
    border: "none",
    "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" },
    "& .MuiDataGrid-row": { cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } },
    "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6", fontSize: "0.875rem" },
  }), [])

  const selectedProducts = useMemo(() => parseProducts(selectedOrder), [selectedOrder, parseProducts])

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 3, py: 2 }}>
        <Container maxWidth={false} disableGutters>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", color: "#6b7280" }}
                onClick={() => navigate("/customers")}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t("Home")}
              </Link>
              <Typography color="#111827" fontWeight={500}>{t("Packaging")}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={fetchOrders} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb" }}>
        <Container maxWidth={false} disableGutters>
          <Tabs
            value={filter}
            onChange={handleTabChange}
            sx={{
              px: 3,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.875rem", minHeight: 48 },
              "& .Mui-selected": { color: "#3b82f6" },
              "& .MuiTabs-indicator": { bgcolor: "#3b82f6" },
            }}
          >
            {FILTER_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={t(tab.label)} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ py: 3 }}>
        <AnalyticsPanel
          analytics={analytics}
          expandedPanels={expandedPanels}
          setExpandedPanels={setExpandedPanels}
          openOrder={openOrder}
          t={t}
        />

        {/* Search */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <TextField
            placeholder={t("Search orders...")}
            value={search}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: 300, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredOrders.length} ${t("Orders")}`} sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 500 }} />
        </Stack>

        {/* Orders DataGrid */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
          <DataGrid
            rows={filteredOrders}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => openOrder(params.row)}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={dataGridSx}
          />
        </Card>
      </Container>

      {/* Order Detail Dialog */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6" }}>
              <LocalShippingIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>{t("Order")} #{selectedOrder?.id}</Typography>
              <Typography variant="body2" color="#6b7280">{selectedOrder?.name}</Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#6b7280">{t("Status")}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedOrder.status}
                      size="small"
                      sx={{ bgcolor: getStatusColor(selectedOrder.status).bg, color: getStatusColor(selectedOrder.status).color, fontWeight: 500 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#6b7280">{t("Delivery Date")}</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedOrder.expected_delivery_date ? moment(selectedOrder.expected_delivery_date).format("YYYY-MM-DD") : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="#6b7280">{t("Address")}</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedOrder.delivery_address || "-"}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t("Products")}</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300, borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "#f9fafb" }}>{t("Product ID")}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "#f9fafb" }}>{t("Name")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: "#f9fafb" }}>{t("Qty")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedProducts === "PARSE_ERROR" ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Alert severity="error" sx={{ py: 0.5 }}>{t("Invalid products data")}</Alert>
                        </TableCell>
                      </TableRow>
                    ) : selectedProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography color="#6b7280" sx={{ py: 2 }}>{t("No products")}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedProducts.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{p.id}</TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell align="right">{p.quantity}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedOrder?.status === "TO-PACKAGE" && (
                <TextField
                  fullWidth
                  label={t("Packaged by (your name)")}
                  value={packagerName}
                  onChange={handlePackagerNameChange}
                  placeholder={t("Type your full name")}
                  disabled={isProcessing}
                  error={Boolean(nameError)}
                  helperText={nameError}
                  sx={{ mb: 2 }}
                />
              )}

              {infoMsg.text && (
                <Alert severity={infoMsg.severity} sx={{ mb: 2 }}>
                  {isProcessing && <CircularProgress size={16} sx={{ mr: 1, verticalAlign: "middle" }} />}
                  {infoMsg.text}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printLabel} disabled={isProcessing}>
            {t("Print Label")}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeModal} disabled={isProcessing}>{t("Cancel")}</Button>
          {selectedOrder?.status === "TO-PACKAGE" ? (
            <Button
              variant="contained"
              color="success"
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={markPackaged}
              disabled={!packagerName.trim() || isProcessing}
            >
              {isProcessing ? t("Processing...") : t("MARK AS PACKAGED")}
            </Button>
          ) : (
            <Chip icon={<CheckCircleIcon />} label={t("Already Packaged")} color="success" variant="filled" />
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default memo(MuiPackaging)
